/**
 * A file picked from the project's Drive ("Browse Drive") travels through the attachments flow as a `File` -- the same type as a
 * file picked from the computer -- so staging (`useAttachmentStaging`), the pending list and every module's upload call work
 * unchanged. It carries no bytes: it only remembers which Drive item it stands for, and the ONE shared upload function
 * (`uploadEntityAttachments`, lib/entity-attachments.ts) hands those to the server, which copies the object inside Storage.
 */
const DRIVE_ITEM_ID = Symbol('driveItemId');

export type DriveFileRef = File & { [DRIVE_ITEM_ID]: string };

export interface DriveFileInfo {
  id: string;
  name: string;
  size?: number | null;
  mime_type?: string | null;
}

export function createDriveFileRef(item: DriveFileInfo): DriveFileRef {
  const ref = new File([], item.name, { type: item.mime_type || '' }) as DriveFileRef;
  // the size the Drive reports, so the pending list shows the real size (an empty File would say 0)
  Object.defineProperty(ref, 'size', { value: Number(item.size) || 0 });
  Object.defineProperty(ref, DRIVE_ITEM_ID, { value: item.id });
  return ref;
}

export function isDriveFileRef(file: unknown): file is DriveFileRef {
  return file instanceof File && typeof (file as DriveFileRef)[DRIVE_ITEM_ID] === 'string';
}

export const driveItemIdOf = (file: DriveFileRef): string => file[DRIVE_ITEM_ID];
